import { Close } from "@mui/icons-material";
import { Button, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography, DialogActions } from "@mui/material";
import dayjs from "dayjs";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { orderStatusFormatted } from "src/constant";
import useAppGlobal from "src/context/useAppGlobal";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import useDebounce from "src/hooks/useDebounce";
import useWarehouseTransferStatus from "src/hooks/warehouse-transfer/useWarehouseTransferStatus";
import useWTCodeScanExport from "src/hooks/warehouse-transfer/useWTCodeScanExport";
import ImportReviewDialog from "../inventory-in/ImportReviewDialog";
import TableComp from "src/components/shared/table/TableComp";
import useExportPrepareProdBody from "./utils/useExportPrepareProdBody";

export default function ExportPrepareProd({ item }) {
  const { closeDialog } = useAppGlobal();
  const [prodPick, setProdPick] = useState();
  const [prodList, setProdList] = useState([]);
  const [indiProd, setIndiProd] = useState();
  const [isPrint, setIsPrint] = useState(null);
  const [confirmAll, setConfirmAll] = useState(true);
  const debounceVal = useDebounce(indiProd?.importItemCodes, 200);

  const { data: batchCode } = useSettingBatchCode();
  const { trigger: scanCode } = useWTCodeScanExport(item?._id);
  const { trigger: exportStatus } = useWarehouseTransferStatus(item?._id);
  const { header, bodyList, sortData, loading, totalTransferDetail, detailMutate, detail } = useExportPrepareProdBody({
    item,
    setProdList,
    prodList,
    setIndiProd,
    indiProd,
    setProdPick,
    prodPick,
  });

  const handleClose = () => {
    closeDialog();
  };

  const handleFilterErrorCode = (key) => {
    setIndiProd((prev) => ({ ...prev, importItemCodes: undefined }));
    closeSnackbar(key);
  };

  const handleDisplayError = (error) => {
    const action = (key) => (
      <Button
        onClick={() => handleFilterErrorCode(key)}
        sx={{ bgcolor: "var(--black)" }}
        variant="contained"
        size="small"
      >
        Xác nhận
      </Button>
    );
    return enqueueSnackbar(
      <div>
        <p style={{ margin: 0 }}>Vui lòng xác nhận xoá các mã lỗi:</p>
        {!!error?.createFailed?.length && (
          <p style={{ margin: 0 }}>
            Mã lỗi: <strong>{String(error?.createFailed)}</strong>
          </p>
        )}
        {!!error?.duplicate?.length && (
          <p style={{ margin: 0 }}>
            Mã trùng: <strong>{String(error?.duplicate)}</strong>
          </p>
        )}
        {!!error?.existed?.length && (
          <p style={{ margin: 0 }}>
            Mã đã tồn tại: <strong>{String(error?.existed)}</strong>
          </p>
        )}
        {!!error?.invalidPrefix?.length && (
          <p style={{ margin: 0 }}>
            Sai format: <strong>{String(error?.invalidPrefix)}</strong>
          </p>
        )}
        {!!error?.notFound?.length && (
          <p style={{ margin: 0 }}>
            Mã không tồn tại: <strong>{String(error?.notFound)}</strong>
          </p>
        )}
        {!!error?.notInBatch?.length && (
          <p style={{ margin: 0 }}>
            Mã không tồn tại trong số lô: <strong>{String(error?.notInBatch)}</strong>
          </p>
        )}
        {error === "BatItemCodeExceed" && <p style={{ margin: 0, fontWeight: "bold" }}>Mã sản phẩm vượt quá số lượng cần nhập</p>}
      </div>,
      {
        variant: "warning",
        action,
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    );
  };

  // SCAN FOR EACH PRODUCT
  const handleScan = async (codes) => {
    const res = await scanCode({
      pathIncluded: {
        transferDetailId: prodPick?._id,
      },
      body: {
        itemCodes: codes,
        note: indiProd?.note,
      },
    });
    if (res?.success) {
      if (res?.data?.itemCodes?.totalError === 0) {
        setIndiProd((prev) => ({ ...prev, importItemCodes: undefined }));
        detailMutate();
      }
      if (res?.data?.totalError > 0 || res?.data?.itemCodes?.totalError > 0) {
        handleDisplayError(res?.data?.errorItemCodes || res?.data?.itemCodes?.errorItemCodes);
      }
    } else {
      return enqueueSnackbar(res?.error?.message);
    }
  };

  const handleFilterCode = () => {
    if (!Array.isArray(batchCode?.data?.prefixItemCodes) || !Array.isArray(batchCode?.data?.stripItemCodes)) return;
    const rawLines = debounceVal.map((v) => v?.replaceAll("Shift", ""));

    const validCodes = [];
    const invalidCodes = [];

    rawLines.forEach((line) => {
      let cleaned = line;

      batchCode.data.stripItemCodes.forEach((strip) => {
        if (cleaned.startsWith(strip + "/")) {
          cleaned = cleaned.replace(strip + "/", "");
        } else if (cleaned.startsWith(strip)) {
          cleaned = cleaned.replace(strip, "");
        }
      });

      const hasValidPrefix = batchCode.data.prefixItemCodes.some((prefix) => cleaned.startsWith(prefix));

      if (hasValidPrefix) {
        validCodes.push(cleaned);
      } else {
        invalidCodes.push(cleaned);
      }
    });

    handleScan(validCodes);
  };

  useEffect(() => {
    if (debounceVal) {
      handleFilterCode();
    }
  }, [debounceVal]);

  const handleExport = async () => {
    const res = await exportStatus({
      body: {
        warehouse: detail?.data?.toWarehouse?._id,
        statusOut: orderStatusFormatted.Exported,
      },
    });
    if (res?.success) {
      setIsPrint(res?.data);
      return enqueueSnackbar("Thành công", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  const hasUnexportedQty = detail?.data?.transferDetails?.some((item) => {
    const exported = item.exportedQty ?? 0;
    return exported !== 0 && exported < item.qty;
  });

  const totalProd = Array.isArray(detail?.data?.transferDetails) ? detail?.data?.transferDetails?.reduce((acc, curr) => acc + curr?.qty, 0) : 0;

  useEffect(() => {
    if (!!prodPick) {
      setConfirmAll(false);
    }
  }, [prodPick]);

  const isScanned = totalTransferDetail?.exportedQty > 0 && totalTransferDetail?.exportedQty < Number(prodPick?.qty);

  const confirmPerItem = (val) => {
    if (!isScanned) {
      const index = prodList?.findIndex((v) => v?.batch === val?.batch);

      const prod = [...prodList];
      prod[index] = { ...prod[index], progress: "Nhập đủ" };
      setProdList(prod);
      setProdPick();
    }
  };

  return (
    <>
      <DialogTitle
        fontWeight="bold"
        display="flex"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid var(--grey)", mb: "8px" }}
      >
        {!isPrint && "Soạn hàng"}
        <IconButton
          onClick={handleClose}
          sx={{ width: "24px", height: "24px", ml: "auto" }}
        >
          <Close sx={{ fontSize: "22px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!isPrint ? (
          <Stack gap={2}>
            <Stack>
              <Typography>
                <strong>Kho nhận: </strong> {item?.toWarehouse?.name ?? "-"}
              </Typography>
              <Typography>
                <strong>Sản phẩm: </strong> {detail?.data?.transferDetails?.length ?? "-"}
              </Typography>
              <Typography>
                <strong>Số lượng: </strong> {totalProd ?? "-"}
              </Typography>
              <Typography>
                <strong>Hình thức thanh toán: </strong> {item?.paymentMethod?.name ?? "-"}
              </Typography>
              <Typography>
                <strong>Thời gian tạo yêu cầu xuất hàng: </strong> {dayjs(item?.createdAt).format("DD/MM/YYYY HH:mm:ss") ?? "-"}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Danh sách sản phẩm</Typography>
            </Stack>
            <Stack>
              <TableComp
                dynamicWidth={"400px"}
                dynamicHeight={"350px"}
                activeRow={prodPick}
                headers={header}
                bodyList={bodyList}
                sortData={sortData}
                isLoading={loading}
              />
            </Stack>
            <Stack>
              <TextField
                value={indiProd?.note || ""}
                multiline
                rows={1}
                label="Ghi chú"
                fullWidth
                onChange={(e) => console.log("first")}
              />
            </Stack>
          </Stack>
        ) : (
          <ImportReviewDialog
            val={isPrint}
            transferFileType={orderStatusFormatted.Exported}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: "0 24px 24px" }}>
        {/* {!prodPick && !isPrint && confirmAll && ( */}
        <Button
          disabled={hasUnexportedQty}
          onClick={handleExport}
          variant="contained"
          fullWidth
        >
          Xác nhận xuất toàn bộ sản phẩm
        </Button>
        {/* // )} */}
        {/* {!isPrint && !!prodPick && !confirmAll && (
          <Button
            disabled={isScanned}
            variant="contained"
            fullWidth
            onClick={() => {
              setConfirmAll(true);
              confirmPerItem(prodPick);
              enqueueSnackbar("Xác nhận xuất thành công", { variant: "success" });
            }}
          >
            Xác nhận xuất sản phẩm
          </Button>
        )} */}
      </DialogActions>
    </>
  );
}
